import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Lock, Check, Repeat, Users } from "lucide-react";
import { sameDay, minutesOf, atMinutes, snap, fmtHM, fmtDur, startOfDay, addDays } from "@/lib/time";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const PX = { compact: 44, comfortable: 64 };
const STEP = 15;

function layoutColumn(items) {
  const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end);
  const placed = [];
  for (const it of sorted) {
    const overlapping = placed.filter((p) => p.item.end > it.start && p.item.start < it.end);
    const used = new Set(overlapping.map((p) => p.col));
    let col = 0; while (used.has(col)) col++;
    placed.push({ item: it, col });
  }
  for (const p of placed) {
    const cluster = placed.filter((q) => q.item.end > p.item.start && q.item.start < p.item.end);
    p.cols = Math.max(...cluster.map((q) => q.col)) + 1;
  }
  return placed;
}

function itemClass(it) {
  if (it.kind === "task_block") {
    return it.done ? "bg-zinc-500/15 border-zinc-500/30 text-zinc-400 line-through" : "bg-indigo-500/20 border-indigo-400/50 text-indigo-100 border-dashed";
  }
  if (it.transparency === "free") return "bg-transparent border-emerald-400/40 text-emerald-200 border-dashed";
  if (it.status === "tentative") return "bg-sky-500/10 border-sky-400/40 text-sky-200 border-dotted";
  if (it.provider === "airtime") return ["PERSONAL", "GOLF"].includes(it.raw.category) ? "bg-emerald-500/20 border-emerald-400/50 text-emerald-100" : "bg-amber-500/20 border-amber-400/50 text-amber-100";
  return "bg-sky-500/20 border-sky-400/50 text-sky-100";
}

export default function WeekGrid({ days, items, density = "comfortable", selectedKey, defaultMinutes = 60,
  onSlotClick, onQuickCreate, onSelectRange, onItemClick, onItemMove, onItemResize, onDragOut, onDropTask, onAllDayClick }) {
  const pxh = PX[density] || PX.comfortable;
  const bodyRef = useRef(null);
  const colsRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const [hover, setHover] = useState(null);
  const [quick, setQuick] = useState(null);
  const [quickTitle, setQuickTitle] = useState("");
  const [now, setNow] = useState(new Date());
  const clickTimer = useRef(null);

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 7.5 * pxh; }, [pxh, days.length]);

  const timed = useMemo(() => items.filter((i) => !i.allDay), [items]);
  const allDay = useMemo(() => items.filter((i) => i.allDay), [items]);

  const posFromPointer = useCallback((clientX, clientY) => {
    const cols = colsRef.current; if (!cols) return null;
    const r = cols.getBoundingClientRect();
    const colW = r.width / days.length;
    const dayIdx = Math.max(0, Math.min(days.length - 1, Math.floor((clientX - r.left) / colW)));
    const min = Math.max(0, Math.min(1440, ((clientY - r.top) / pxh) * 60));
    const inside = clientX >= r.left - 8 && clientX <= r.right + 8 && clientY >= r.top - 40 && clientY <= r.bottom + 40;
    return { dayIdx, min, inside };
  }, [days.length, pxh]);

  // ---- pointer interactions (select / move / resize) ----
  const onPointerDownEmpty = (e) => {
    if (e.button !== 0) return;
    const p = posFromPointer(e.clientX, e.clientY); if (!p) return;
    setQuick(null);
    setDrag({ mode: "select", dayIdx: p.dayIdx, startMin: snap(p.min, STEP), curMin: snap(p.min, STEP), moved: false, x: e.clientX, y: e.clientY });
  };
  const onPointerDownItem = (e, it, mode) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    if (!it.editable) { setDrag({ mode: "click", item: it }); return; }
    const p = posFromPointer(e.clientX, e.clientY); if (!p) return;
    const dur = (it.end - it.start) / 60000;
    setDrag({ mode, item: it, dur, offset: p.min - minutesOf(it.start), dayIdx: days.findIndex((d) => sameDay(d, it.start)),
      startMin: minutesOf(it.start), endMin: minutesOf(it.start) + dur, moved: false, x: e.clientX, y: e.clientY, inside: true });
  };

  useEffect(() => {
    if (!drag) return;
    const move = (e) => {
      const p = posFromPointer(e.clientX, e.clientY); if (!p) return;
      const moved = drag.moved || Math.abs(e.clientX - drag.x) > 4 || Math.abs(e.clientY - drag.y) > 4;
      if (drag.mode === "select") setDrag({ ...drag, curMin: snap(p.min, STEP), moved });
      else if (drag.mode === "move") {
        const s = Math.max(0, Math.min(1440 - drag.dur, snap(p.min - drag.offset, STEP)));
        setDrag({ ...drag, dayIdx: p.dayIdx, startMin: s, endMin: s + drag.dur, moved, inside: p.inside });
      } else if (drag.mode === "resize") {
        setDrag({ ...drag, endMin: Math.max(drag.startMin + STEP, Math.min(1440, snap(p.min, STEP))), moved });
      }
    };
    const up = () => {
      const d = drag; setDrag(null);
      if (d.mode === "click") { onItemClick?.(d.item); return; }
      if (d.mode === "select") {
        const a = Math.min(d.startMin, d.curMin), b = Math.max(d.startMin, d.curMin);
        const day = days[d.dayIdx];
        if (!d.moved || b - a < STEP) {
          // defer single click so a double-click can take over (inline quick create)
          clearTimeout(clickTimer.current);
          clickTimer.current = setTimeout(() => onSlotClick?.(atMinutes(day, a), atMinutes(day, Math.min(1440, a + defaultMinutes))), 260);
        } else onSelectRange?.(atMinutes(day, a), atMinutes(day, b));
        return;
      }
      if (d.mode === "move") {
        if (!d.moved) { onItemClick?.(d.item); return; }
        if (!d.inside && d.item.kind === "task_block") { onDragOut?.(d.item); return; }
        const ns = atMinutes(days[d.dayIdx], d.startMin);
        if (ns.getTime() !== d.item.start.getTime()) onItemMove?.(d.item, ns, atMinutes(days[d.dayIdx], d.endMin));
        return;
      }
      if (d.mode === "resize" && d.moved) {
        const day = startOfDay(d.item.start);
        onItemResize?.(d.item, atMinutes(day, d.endMin));
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [drag, days, posFromPointer, onSlotClick, onSelectRange, onItemClick, onItemMove, onItemResize, onDragOut, defaultMinutes]);

  // ---- hover: free gap indicator ----
  const onHover = (e) => {
    if (drag) return;
    const p = posFromPointer(e.clientX, e.clientY); if (!p) { setHover(null); return; }
    const day = days[p.dayIdx];
    const dayItems = timed.filter((i) => sameDay(i.start, day) && i.transparency !== "free" && !i.done);
    if (dayItems.some((i) => minutesOf(i.start) <= p.min && minutesOf(i.start) + (i.end - i.start) / 60000 > p.min)) { setHover(null); return; }
    let gs = 0, ge = 1440;
    for (const i of dayItems) { const s = minutesOf(i.start), en = s + (i.end - i.start) / 60000; if (en <= p.min) gs = Math.max(gs, en); if (s >= p.min) ge = Math.min(ge, s); }
    setHover({ dayIdx: p.dayIdx, min: p.min, gs, ge });
  };

  // ---- external drop from backlog ----
  const onDrop = (e) => {
    const raw = e.dataTransfer.getData("application/airtime-task"); if (!raw) return;
    e.preventDefault();
    const p = posFromPointer(e.clientX, e.clientY); if (!p) return;
    onDropTask?.(JSON.parse(raw), atMinutes(days[p.dayIdx], snap(p.min, STEP)));
  };

  const submitQuick = () => {
    if (quick && quickTitle.trim()) {
      const s = atMinutes(days[quick.dayIdx], quick.startMin);
      onQuickCreate?.(quickTitle.trim(), s, atMinutes(days[quick.dayIdx], Math.min(1440, quick.startMin + defaultMinutes)));
    }
    setQuick(null); setQuickTitle("");
  };

  const gridCols = `repeat(${days.length}, minmax(0, 1fr))`;

  return (
    <div className="flex flex-col rounded-2xl border border-border bg-card overflow-hidden select-none" data-testid="week-grid">
      {/* header */}
      <div className="flex border-b border-border">
        <div className="w-14 shrink-0" />
        <div className="flex-1 grid" style={{ gridTemplateColumns: gridCols }}>
          {days.map((d) => {
            const today = sameDay(d, now);
            return (
              <div key={d.toISOString()} data-testid={`grid-day-${d.getDay()}`} className={`py-2.5 text-center border-l border-border ${today ? "bg-primary/5" : ""}`}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{d.toLocaleDateString("fr-FR", { weekday: "short" })}</div>
                <div className={`font-display text-lg font-semibold leading-tight ${today ? "text-primary" : ""}`}>{d.getDate()}</div>
              </div>
            );
          })}
        </div>
      </div>
      {/* all-day band */}
      <div className="flex border-b border-border bg-background/40">
        <div className="w-14 shrink-0 font-mono text-[9px] uppercase tracking-wider text-muted-foreground pt-1.5 text-right pr-2">Journée</div>
        <div className="flex-1 grid min-h-[30px]" style={{ gridTemplateColumns: gridCols }} data-testid="allday-band">
          {days.map((d) => (
            <div key={d.toISOString()} className="border-l border-border p-1 space-y-1 cursor-pointer" onClick={() => onAllDayClick?.(d)}>
              {allDay.filter((i) => i.startDate <= d && addDays(i.endDate, 0) > d).map((i) => (
                <div key={i.key} data-testid={`allday-item-${i.key}`} onClick={(e) => { e.stopPropagation(); onItemClick?.(i); }}
                  className={`truncate rounded-md border px-2 py-0.5 text-[11px] cursor-pointer ${itemClass(i)}`} title={i.title}>
                  {i.transparency === "free" ? "○ " : ""}{i.title}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      {/* body */}
      <div ref={bodyRef} className="flex-1 overflow-y-auto airtime-scroll relative" style={{ maxHeight: "calc(100vh - 290px)", minHeight: 360 }}>
        <div className="flex" style={{ height: 24 * pxh }}>
          <div className="w-14 shrink-0 relative">
            {HOURS.map((h) => (
              <div key={h} className="absolute right-2 -translate-y-1/2 font-mono text-[10px] text-muted-foreground" style={{ top: h * pxh }}>{h ? `${String(h).padStart(2, "0")}:00` : ""}</div>
            ))}
          </div>
          <div ref={colsRef} className="flex-1 grid relative" style={{ gridTemplateColumns: gridCols }}
            onPointerDown={onPointerDownEmpty} onPointerMove={onHover} onPointerLeave={() => setHover(null)}
            onDragOver={(e) => { if (e.dataTransfer.types.includes("application/airtime-task")) e.preventDefault(); }} onDrop={onDrop}
            onDoubleClick={(e) => { clearTimeout(clickTimer.current); const p = posFromPointer(e.clientX, e.clientY); if (p) { setQuick({ dayIdx: p.dayIdx, startMin: snap(p.min, STEP) }); setQuickTitle(""); } }}>
            {days.map((d, di) => {
              const dayItems = timed.filter((i) => sameDay(i.start, d) && !(drag?.mode === "move" && drag.item.key === i.key && drag.moved));
              const placed = layoutColumn(dayItems);
              const today = sameDay(d, now);
              return (
                <div key={d.toISOString()} className={`relative border-l border-border ${today ? "bg-primary/[0.03]" : ""}`} data-testid={`grid-col-${di}`}>
                  {HOURS.map((h) => <div key={h} className="absolute inset-x-0 border-t border-border/60" style={{ top: h * pxh }} />)}
                  {HOURS.map((h) => <div key={`h${h}`} className="absolute inset-x-0 border-t border-dashed border-border/30" style={{ top: h * pxh + pxh / 2 }} />)}
                  {today && <div className="absolute inset-x-0 z-20 border-t-2 border-rose-500 pointer-events-none" style={{ top: (minutesOf(now) / 60) * pxh }}><span className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-rose-500" /></div>}
                  {placed.map(({ item: it, col, cols }) => {
                    const s = minutesOf(it.start), dur = (it.end - it.start) / 60000;
                    const isResizing = drag?.mode === "resize" && drag.item.key === it.key;
                    const endMin = isResizing ? drag.endMin : s + dur;
                    const w = 100 / cols;
                    return (
                      <div key={it.key} data-testid={`cal-item-${it.key}`}
                        onPointerDown={(e) => onPointerDownItem(e, it, "move")}
                        className={`absolute rounded-lg border px-2 py-1 text-[11px] leading-tight overflow-hidden group z-10 transition-shadow
                          ${itemClass(it)} ${it.editable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} ${selectedKey === it.key ? "ring-2 ring-primary shadow-lg" : "hover:shadow-md"}`}
                        style={{ top: (s / 60) * pxh + 1, height: Math.max(18, ((endMin - s) / 60) * pxh - 2), left: `calc(${col * w}% + 2px)`, width: `calc(${w}% - 4px)` }}>
                        <div className="flex items-center gap-1 font-medium truncate">
                          {it.kind === "task_block" && (it.done ? <Check className="w-3 h-3 shrink-0" /> : <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />)}
                          {it.locked && <Lock className="w-3 h-3 shrink-0" />}
                          {it.raw?.is_occurrence && <Repeat className="w-3 h-3 shrink-0 opacity-70" />}
                          {it.raw?.attendees?.length > 0 && <Users className="w-3 h-3 shrink-0 opacity-70" />}
                          <span className="truncate">{it.title}</span>
                        </div>
                        {dur >= 40 && <div className="font-mono text-[10px] opacity-70">{fmtHM(it.start)} – {fmtHM(new Date(it.start.getTime() + (endMin - s) * 60000))}{it.status === "tentative" ? " · provisoire" : ""}</div>}
                        {it.editable && <div data-testid={`resize-${it.key}`} onPointerDown={(e) => onPointerDownItem(e, it, "resize")}
                          className="absolute inset-x-0 bottom-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 bg-current/20" />}
                      </div>
                    );
                  })}
                  {/* selection ghost */}
                  {drag?.mode === "select" && drag.dayIdx === di && drag.moved && (
                    <div className="absolute inset-x-1 rounded-lg bg-primary/20 border border-primary/50 z-30 pointer-events-none px-2 py-1 font-mono text-[10px] text-primary"
                      style={{ top: (Math.min(drag.startMin, drag.curMin) / 60) * pxh, height: (Math.abs(drag.curMin - drag.startMin) / 60) * pxh }}>
                      {fmtHM(atMinutes(d, Math.min(drag.startMin, drag.curMin)))} – {fmtHM(atMinutes(d, Math.max(drag.startMin, drag.curMin)))}
                    </div>
                  )}
                  {/* move ghost */}
                  {drag?.mode === "move" && drag.moved && drag.dayIdx === di && (
                    <div className={`absolute inset-x-1 rounded-lg border-2 z-30 pointer-events-none px-2 py-1 text-[11px] ${drag.inside ? "border-primary bg-primary/20" : "border-rose-400 bg-rose-500/10 opacity-60"}`}
                      style={{ top: (drag.startMin / 60) * pxh, height: (drag.dur / 60) * pxh }}>
                      <div className="font-medium truncate">{drag.item.title}</div>
                      <div className="font-mono text-[10px] opacity-80">{fmtHM(atMinutes(d, drag.startMin))} – {fmtHM(atMinutes(d, drag.endMin))}</div>
                    </div>
                  )}
                  {/* hover free-slot label */}
                  {hover && hover.dayIdx === di && !drag && hover.ge - hover.gs >= 30 && (
                    <div className="absolute right-1 z-20 pointer-events-none rounded-md bg-background/90 border border-border px-1.5 py-0.5 font-mono text-[9px] text-emerald-400 whitespace-nowrap"
                      style={{ top: (hover.min / 60) * pxh - 10 }} data-testid="free-slot-hint">Libre {fmtDur(hover.ge - hover.gs)}</div>
                  )}
                  {/* inline quick create */}
                  {quick && quick.dayIdx === di && (
                    <div className="absolute inset-x-1 z-40 rounded-lg border border-primary bg-card shadow-xl p-1.5" style={{ top: (quick.startMin / 60) * pxh }} onPointerDown={(e) => e.stopPropagation()}>
                      <input autoFocus data-testid="quick-create-input" value={quickTitle} onChange={(e) => setQuickTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") submitQuick(); if (e.key === "Escape") { setQuick(null); setQuickTitle(""); } }}
                        onBlur={() => { if (!quickTitle.trim()) setQuick(null); }}
                        placeholder="Ajouter… (Entrée = tâche)" className="w-full bg-transparent text-xs outline-none" />
                      <div className="font-mono text-[9px] text-muted-foreground mt-0.5">{fmtHM(atMinutes(d, quick.startMin))} · {fmtDur(defaultMinutes)}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
